import { keyframes } from "@emotion/react";
import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";

// Shown while the report data loads. The agent's brand colors arrive with that data, so this
// screen sticks to neutral colors that sit well next to any brand.
const INK = "#1d2433";
const MUTED = "#6b7385";
const TRACK = "#e6e9ef";

// Only describe what the report actually loads: the home, its current estimate and value history.
const STEPS = [
	"Looking up your home",
	"Getting today's estimate",
	"Loading your value history",
	"Putting your report together",
];

// The house outline and the value line inside it draw in, hold, then fade before the loop repeats.
const drawHouse = keyframes`
	0% { stroke-dashoffset: 1; opacity: 1; }
	38% { stroke-dashoffset: 0; opacity: 1; }
	86% { stroke-dashoffset: 0; opacity: 1; }
	100% { stroke-dashoffset: 0; opacity: 0; }
`;
const drawLine = keyframes`
	0%, 30% { stroke-dashoffset: 1; opacity: 1; }
	62% { stroke-dashoffset: 0; opacity: 1; }
	86% { stroke-dashoffset: 0; opacity: 1; }
	100% { stroke-dashoffset: 0; opacity: 0; }
`;
const popDot = keyframes`
	0%, 58% { transform: scale(0); opacity: 0; }
	66% { transform: scale(1.25); opacity: 1; }
	72%, 86% { transform: scale(1); opacity: 1; }
	100% { transform: scale(1); opacity: 0; }
`;
const slide = keyframes`
	0% { transform: translateX(-100%); }
	100% { transform: translateX(260%); }
`;
const fadeUp = keyframes`
	from { opacity: 0; transform: translateY(4px); }
	to { opacity: 1; transform: translateY(0); }
`;

const CYCLE = "2.8s";
const reducedMotion = "@media (prefers-reduced-motion: reduce)";

export default function ReportLoader() {
	const [step, setStep] = useState(0);
	useEffect(() => {
		const timer = window.setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 1800);
		return () => window.clearInterval(timer);
	}, []);

	const stroke = {
		fill: "none",
		strokeLinecap: "round" as const,
		strokeLinejoin: "round" as const,
		strokeDasharray: 1,
	};

	return (
		<Box
			role="status"
			aria-live="polite"
			sx={{
				m: "-15px",
				minHeight: "100vh",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: "22px",
				px: "24px",
				textAlign: "center",
				background: "radial-gradient(circle at 50% 38%, #ffffff 0%, #f5f7fa 62%)",
				color: INK,
			}}
		>
			<Box
				component="svg"
				viewBox="0 0 96 96"
				aria-hidden="true"
				sx={{
					width: 96,
					height: 96,
					"& .house": { animation: `${drawHouse} ${CYCLE} cubic-bezier(.65,0,.35,1) infinite` },
					"& .line": { animation: `${drawLine} ${CYCLE} cubic-bezier(.65,0,.35,1) infinite` },
					"& .dot": {
						transformOrigin: "64px 50px",
						transformBox: "view-box",
						animation: `${popDot} ${CYCLE} ease-out infinite`,
					},
					[reducedMotion]: {
						"& .house, & .line": { animation: "none", strokeDashoffset: 0 },
						"& .dot": { animation: "none", opacity: 1, transform: "none" },
					},
				}}
			>
				{/* Soft ground shadow */}
				<ellipse cx="48" cy="84" rx="26" ry="3" fill={TRACK} />
				{/* Roof and walls */}
				<path
					className="house"
					pathLength={1}
					d="M14 44 L48 16 L82 44 M22 38 V78 H74 V38"
					stroke={INK}
					strokeWidth={4}
					{...stroke}
				/>
				{/* Rising value line */}
				<path
					className="line"
					pathLength={1}
					d="M32 64 L42 58 L50 61 L64 50"
					stroke={MUTED}
					strokeWidth={3.5}
					{...stroke}
				/>
				<circle className="dot" cx="64" cy="50" r="4" fill={INK} />
			</Box>

			<Box>
				<Typography
					component="h1"
					sx={{ fontSize: "19px", fontWeight: 700, letterSpacing: "-.01em", color: INK }}
				>
					Preparing your home value report
				</Typography>
				<Typography
					key={step}
					sx={{
						mt: "6px",
						minHeight: "20px",
						fontSize: "14px",
						color: MUTED,
						animation: `${fadeUp} .4s ease-out`,
						[reducedMotion]: { animation: "none" },
					}}
				>
					{STEPS[step]}
				</Typography>
			</Box>

			<Box
				aria-hidden="true"
				sx={{
					position: "relative",
					width: 160,
					height: 3,
					borderRadius: 99,
					overflow: "hidden",
					backgroundColor: TRACK,
				}}
			>
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						width: "40%",
						borderRadius: 99,
						backgroundColor: INK,
						animation: `${slide} 1.4s cubic-bezier(.65,0,.35,1) infinite`,
						[reducedMotion]: { animation: "none", width: "100%", opacity: 0.25 },
					}}
				/>
			</Box>
		</Box>
	);
}
